import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, Scissors, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // challenge_token when 2FA required
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const { loginAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const finish = (data) => {
    loginAdmin(data.token);
    toast.success("Signed in");
    const from = location.state?.from;
    navigate(typeof from === "string" && from.startsWith("/admin/dashboard") ? from : "/admin/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.mfa_required) {
        setChallenge(data.challenge_token);
      } else {
        finish(data);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { challenge_token: challenge };
      if (useRecovery) payload.recovery_code = code.trim().toUpperCase();
      else payload.code = code.trim();
      const { data } = await axios.post(`${API}/auth/login/mfa`, payload);
      finish(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#161616] p-6">
      <div className="w-full max-w-sm bg-[#212120] rounded-lg p-8 border border-[#2f2f2d]">
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#D4A359] mb-8">
          <Scissors className="h-4 w-4" /> Craft Studio · Atelier
        </div>

        {!challenge ? (
          <>
            <h2 className="font-serif text-3xl text-[#F8F6F0]">Seller sign in</h2>
            <p className="text-[#8C867B] mt-2 text-sm">Manage products, frameworks and customer designs.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#8C867B]">Email</Label>
                <Input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="h-12 bg-[#161616] border-[#3a3a37] text-[#F8F6F0] focus-visible:ring-[#D4A359]" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#8C867B]">Password</Label>
                <Input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="h-12 bg-[#161616] border-[#3a3a37] text-[#F8F6F0] focus-visible:ring-[#D4A359]" />
              </div>
              <Button data-testid="admin-login-button" type="submit" disabled={loading}
                className="w-full h-12 rounded-full bg-[#D4A359] hover:bg-[#c2924a] text-[#161616] font-medium active:scale-[0.98] transition-transform duration-100">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h2 className="font-serif text-3xl text-[#F8F6F0] flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-[#D4A359]" /> Verify it's you</h2>
            <p className="text-[#8C867B] mt-2 text-sm">
              {useRecovery ? "Enter one of your one-time recovery codes." : "Enter the 6-digit code from your authenticator app."}
            </p>
            <form onSubmit={submitMfa} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#8C867B]">{useRecovery ? "Recovery code" : "Authenticator code"}</Label>
                <Input
                  data-testid="mfa-code-input"
                  value={code}
                  onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, ""))}
                  maxLength={useRecovery ? 12 : 6}
                  autoFocus
                  placeholder={useRecovery ? "XXXX-XXXX" : "123456"}
                  className="h-12 bg-[#161616] border-[#3a3a37] text-[#F8F6F0] font-mono tracking-[0.3em] text-center focus-visible:ring-[#D4A359]" />
              </div>
              <Button data-testid="mfa-verify-button" type="submit" disabled={loading || (!useRecovery && code.length !== 6)}
                className="w-full h-12 rounded-full bg-[#D4A359] hover:bg-[#c2924a] text-[#161616] font-medium active:scale-[0.98] transition-transform duration-100">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
              <button type="button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }} data-testid="mfa-toggle-recovery"
                className="w-full font-mono text-xs uppercase tracking-widest text-[#8C867B] hover:text-[#D4A359]">
                {useRecovery ? "← Use authenticator code" : "Use a recovery code instead"}
              </button>
            </form>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-[#2f2f2d]">
          <Link to="/" className="font-mono text-xs uppercase tracking-widest text-[#8C867B] hover:text-[#D4A359]">
            ← Customer portal
          </Link>
        </div>
      </div>
    </div>
  );
}
